import { createBrowserRouter } from 'react-router-dom';
import React from 'react';
import App from './App';
import { LoginPage } from '@/features/auth/components/LoginPage';
import { SignupPage } from '@/features/auth/components/SignupPage';
import { VerifyEmailPage } from '@/features/auth/components/VerifyEmailPage';
import { EmailVerificationSuccess } from '@/features/auth/components/EmailVerificationSuccess';
import { AuthActionHandlerPage } from '@/features/auth/components/AuthActionHandlerPage.tsx';
import { DashboardLayout } from '@/features/dashboard/components/DashboardLayout';
import { Dashboard } from '@/features/dashboard/components/Dashboard';
import { Settings } from '@/features/settings/components/Settings';
import FormBuilder from '@/features/form-builder/components/FormBuilder';
import { LandingPage } from '@/features/landing-page/components/LandingPage';
import { CrmCallback } from '@/features/auth/components/CrmCallback';
import { CheckoutSuccessPage } from '@/features/auth/components/CheckoutSuccessPage';
import { ApplicationForm } from '@/features/application/components/ApplicationForm';
import { NotFound } from '@/features/help/components/NotFound';
import { LegalPage } from '@/features/legal/components/LegalPage';
import { DashboardLogoPage } from '@/features/dashboard/components/LogoEditor';
import ProviderSelectionModal from '@/features/auth/components/ProviderSelectionModal';
import { dashboardLoader } from '@/features/dashboard/loaders/dashboard.loader';
import { FullScreenLoader } from '@/features/ui/components/FullScreenLoader';
import { logger } from '@/features/help/utils/logger';
import { SubmissionCompletePage } from '@/features/application/components/SubmissionCompletePage';

// Purpose: Component to handle invalid routes
const InvalidRoute = () => {
  logger.warn('Router', 'Invalid route accessed', {
    path: window.location.pathname,
  });
  return <NotFound />;
};

export const router = createBrowserRouter(
  [
    {
      path: '/',
      element: <App />,
      children: [
        // Public routes
        { index: true, element: <LandingPage /> },
        { path: 'login', element: <LoginPage /> },
        { path: 'signup', element: <SignupPage /> },
        { path: 'verify-email', element: <VerifyEmailPage /> },
        { path: 'email-success', element: <EmailVerificationSuccess /> },
        { path: 'auth/action', element: <AuthActionHandlerPage /> },
        { path: 'crm/callback', element: <CrmCallback /> },
        { path: 'checkout-success', element: <CheckoutSuccessPage /> },
        { path: 'select-provider', element: <ProviderSelectionModal /> },

        // Legal pages
        { path: 'legal', element: <LegalPage /> },
        { path: 'legal/:documentType', element: <LegalPage /> },

        // Application Form Route (record and signer now via query params: crmId & signerIndex)
        // Custom domain validation is handled inline in ApplicationForm
        { path: 'application/:userShortId/:mappingShortId', element: <ApplicationForm /> },
        {
          path: 'application/:userShortId/:mappingShortId/submission-complete',
          element: <SubmissionCompletePage />,
        },

        // Protected dashboard routes with loader
        {
          path: 'dashboard',
          element: <DashboardLayout />,
          loader: dashboardLoader,
          children: [
            { index: true, element: <Dashboard /> },
            { path: 'field-mapping', element: <FormBuilder /> },
            { path: 'logo', element: <DashboardLogoPage /> },
            { path: 'settings', element: <Settings /> },
          ],
        },

        // Catch all unknown routes
        { path: '*', element: <InvalidRoute /> },
      ],
    },
  ],
  {
    // Shows while ANY route is loading (loaders, navigation, lazy imports, etc.)
    fallbackElement: <FullScreenLoader message="Loading..." showOverlay={false} />,
  } as any
);
